import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Printer, TrendingDown, TrendingUp } from 'lucide-react';
import { formatMoney, type DailyReportRow } from '@bakery/shared';

import { api } from '../lib/api.js';
import { printDailyReport } from '../lib/printing.js';
import { Button, Card, cx, EmptyState, Input, Skeleton } from '../components/ui.js';

/**
 * What each day sold, and what it actually left.
 *
 * The dashboard answers "how is today going" and the expenses screen answers
 * "what did we pay out this month". Neither answers the one the shop keeps
 * asking, which is whether a given Tuesday made money — because the costs that
 * decide it do not arrive daily. Rent comes once a month and wages twice, so a
 * report that charged them to the day they were paid would show one ruinous
 * Thursday and thirty flattering days around it.
 *
 * So each standing cost is spread over the days it buys, and every day carries
 * its share. The month's figures still add up to what was actually paid, which
 * is the point: this screen has to agree with Expenses to the centavo or it
 * just starts an argument.
 */
export function DailyReportScreen() {
  const [month, setMonth] = useState(() => new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString().slice(0, 7));

  const report = useQuery({
    queryKey: ['daily-report', month],
    queryFn: () => api.analytics.dailyReport(month),
  });

  const shift = (by: number) => {
    const [year, index] = month.split('-').map(Number);
    const moved = new Date(Date.UTC(year!, index! - 1 + by, 1));
    setMonth(moved.toISOString().slice(0, 7));
  };

  const label = useMemo(
    () => new Date(`${month}-01T12:00:00Z`).toLocaleDateString('en-PH', { month: 'long', year: 'numeric' }),
    [month],
  );

  const totals = report.data?.totals;

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-wrap items-center gap-3 border-b border-[var(--color-line)] px-4 py-3">
        <div className="min-w-0 flex-1">
          <h1 className="font-display text-lg font-semibold">Daily report</h1>
          <p className="text-xs text-[var(--color-ink-soft)]">
            {label}
            {totals && (
              <>
                {' '}
                · {formatMoney(totals.sales)} taken · {formatMoney(totals.netProfit)} kept
              </>
            )}
          </p>
        </div>

        <div className="flex items-center gap-1.5">
          <Button size="sm" variant="ghost" aria-label="Previous month" onClick={() => shift(-1)} icon={<ChevronLeft className="size-4" />} />
          <Input type="month" value={month} onChange={(event) => setMonth(event.target.value)} className="w-40" />
          <Button size="sm" variant="ghost" aria-label="Next month" onClick={() => shift(1)} icon={<ChevronRight className="size-4" />} />
          <Button
            size="sm"
            variant="primary"
            icon={<Printer className="size-4" />}
            disabled={!report.data}
            onClick={() => report.data && printDailyReport(report.data, label)}
          >
            Print
          </Button>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-auto p-4">
        {report.isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 8 }).map((_, index) => (
              <Skeleton key={index} className="h-11 w-full" />
            ))}
          </div>
        )}

        {report.isError && (
          <EmptyState
            title="The report could not be built"
            description={(report.error as Error)?.message ?? 'Try again in a moment.'}
          />
        )}

        {report.data && totals && (
          <div className="space-y-4">
            <Totals totals={totals} tradingDays={report.data.tradingDays} />

            {/*
              * A table, not cards. Reading down a column to find the bad day is
              * the whole reason to open this screen, and cards make that
              * impossible. It scrolls sideways on a phone instead.
              */}
            <Card className="overflow-x-auto p-0">
              <table className="w-full min-w-[46rem] border-collapse text-sm">
                <thead>
                  <tr className="border-b border-[var(--color-line)] text-left text-[0.68rem] uppercase tracking-wide text-[var(--color-ink-faint)]">
                    <th className="px-3 py-2 font-medium">Day</th>
                    <th className="px-2 py-2 text-right font-medium">Orders</th>
                    <th className="px-2 py-2 text-right font-medium">Sales</th>
                    <th className="px-2 py-2 text-right font-medium">Ingredients</th>
                    <th className="px-2 py-2 text-right font-medium">Gross</th>
                    <th className="px-2 py-2 text-right font-medium">Costs</th>
                    <th className="px-3 py-2 text-right font-medium">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {report.data.rows.map((row) => (
                    <DayRow key={row.date} row={row} />
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-[var(--color-line)] font-semibold">
                    <td className="px-3 py-2.5">Month</td>
                    <td className="px-2 py-2.5 text-right tabular-nums">{totals.orderCount}</td>
                    <td className="px-2 py-2.5 text-right tabular-nums">{formatMoney(totals.sales)}</td>
                    <td className="px-2 py-2.5 text-right tabular-nums">{formatMoney(totals.costOfGoods)}</td>
                    <td className="px-2 py-2.5 text-right tabular-nums">{formatMoney(totals.grossProfit)}</td>
                    <td className="px-2 py-2.5 text-right tabular-nums">{formatMoney(totals.costs)}</td>
                    <td
                      className={cx(
                        'px-3 py-2.5 text-right tabular-nums',
                        totals.netProfit < 0 && 'text-[var(--color-danger)]',
                      )}
                    >
                      {formatMoney(totals.netProfit)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </Card>

            <p className="px-1 text-xs leading-relaxed text-[var(--color-ink-faint)]">
              Rent, bills and the wage bill do not arrive daily, so each is spread over the days it buys —
              the standing costs across this month&rsquo;s {report.data.tradingDays} trading days, and each
              payslip across the days it covers. Nothing is charged to a day the shop was shut.
              Ingredients are what the goods sold that day cost to make, read from the recipes, so the
              month&rsquo;s deliveries are not counted a second time.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/** The four figures worth seeing before any single day. */
function Totals({
  totals,
  tradingDays,
}: {
  totals: Omit<DailyReportRow, 'date' | 'isOpenDay'>;
  tradingDays: number;
}) {
  const perDay = tradingDays > 0 ? Math.round(totals.netProfit / tradingDays) : 0;

  const figures = [
    { label: 'Sales', value: formatMoney(totals.sales), note: `${totals.orderCount} orders` },
    { label: 'Gross profit', value: formatMoney(totals.grossProfit), note: 'after ingredients' },
    { label: 'Costs', value: formatMoney(totals.costs), note: 'rent, wages, one-offs' },
    {
      label: 'Net profit',
      value: formatMoney(totals.netProfit),
      note: `${formatMoney(perDay)} a trading day`,
      strong: true,
      down: totals.netProfit < 0,
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {figures.map((figure) => (
        <Card key={figure.label} className="p-3">
          <p className="text-[0.68rem] uppercase tracking-wide text-[var(--color-ink-faint)]">{figure.label}</p>
          <p
            className={cx(
              'mt-1 font-display text-lg font-semibold tabular-nums',
              figure.down && 'text-[var(--color-danger)]',
            )}
          >
            {figure.strong && (
              figure.down ? (
                <TrendingDown className="mr-1 inline size-4 align-[-0.2em]" />
              ) : (
                <TrendingUp className="mr-1 inline size-4 align-[-0.2em]" />
              )
            )}
            {figure.value}
          </p>
          <p className="mt-0.5 text-xs text-[var(--color-ink-faint)]">{figure.note}</p>
        </Card>
      ))}
    </div>
  );
}

/*
 * A closed day is shown rather than filtered out. A gap in the dates makes the
 * reader wonder what was left out, where a row marked "closed" answers it — and
 * it is also the check that no cost landed on a day nobody worked.
 */
function DayRow({ row }: { row: DailyReportRow }) {
  const date = new Date(`${row.date}T12:00:00Z`);
  const weekday = date.toLocaleDateString('en-PH', { weekday: 'short' });
  const day = date.toLocaleDateString('en-PH', { day: 'numeric', month: 'short' });
  const quiet = row.orderCount === 0;

  return (
    <tr
      className={cx(
        'border-b border-[var(--color-line)] last:border-0',
        !row.isOpenDay && 'bg-[var(--color-surface-sunken)] text-[var(--color-ink-faint)]',
      )}
    >
      <td className="whitespace-nowrap px-3 py-2">
        {day} <span className="text-xs text-[var(--color-ink-faint)]">{weekday}</span>
      </td>
      <td className="px-2 py-2 text-right tabular-nums">
        {row.isOpenDay ? (
          (row.orderCount || <span className="text-[var(--color-ink-faint)]">—</span>)
        ) : (
          <span className="text-xs">closed</span>
        )}
      </td>
      <Money value={row.sales} dim={quiet} />
      <Money value={row.costOfGoods} dim={quiet} />
      <Money value={row.grossProfit} dim={quiet} />
      <Money value={row.costs} dim={row.costs === 0} />
      <td
        className={cx(
          'px-3 py-2 text-right font-medium tabular-nums',
          row.netProfit < 0 && 'text-[var(--color-danger)]',
        )}
      >
        {row.netProfit === 0 ? <span className="font-normal text-[var(--color-ink-faint)]">—</span> : formatMoney(row.netProfit)}
      </td>
    </tr>
  );
}

/** A zero is shown as a dash, because thirty rows of ₱0.00 hide the real ones. */
function Money({ value, dim }: { value: number; dim: boolean }) {
  return (
    <td className="px-2 py-2 text-right tabular-nums">
      {value === 0 && dim ? <span className="text-[var(--color-ink-faint)]">—</span> : formatMoney(value)}
    </td>
  );
}
